import {
  call,
  CallEffect,
  put,
  PutEffect,
  takeLatest,
} from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import {
  GcashApplicationDocumentResponse,
  GcashCreateDocumentRequest,
  GcashCreatePageRequest,
  GcashDocumentPages,
  GcashDocumentsStaticticsResponse,
  GcashDocumentsStatusResponse,
  GcashDocumentTypesResponse,
  GcashExportDocumentRequest,
  GcashExportDocumentsRequest,
  GetPaginatedGcashApplicationDocumentsParams,
  GetPaginatedGcashApplicationDocumentsResponse,
  GetPaginatedGcashApplicationsParams,
  GetPaginatedGcashApplicationsResponse,
  PaginatedGcashActionLogsResponse,
  PaginatedGcashDocumentsByStatusResponse,
  PaginationGcashWithStatusRequest,
  GcashApplicationResponse,
} from '../api/types';
import { PayloadAction } from '@reduxjs/toolkit';
import { getArrayApiErrors } from 'utils/get-array-api-errors';
import { toast } from 'react-toastify';
import { extractErrorMessage } from 'utils/extract-error-message.util';
import { gcashActions } from 'features/gcash/redux/gcash.actions';
import { trackEvent } from 'utils/amplitude.util';
import { AmplitudeEvents } from 'shared/enums/amplitude-events.enum';
import {
  changeGcashDocumentStatus,
  createGcashApplication,
  createGcashApplicationDocument,
  createGcashNewPage,
  exportGcashApplication,
  exportGcashDocumentByStatus,
  exportGcashSingleDocument,
  getGcashApplication,
  getGcashApplicationDocuments,
  getGcashApplications,
  getGcashDocumentInfo,
  getGcashDocumentPages,
  getGcashDocumentsByStatus,
  getGcashDocumentsStatus,
  getGcashDocumentTypes,
  getGcashLogs,
  getGcashStatistics,
} from 'features/gcash/api';
import { GcashDocumentStatuses } from 'features/gcash/enums/gcash-document-statuses.enum';
import { GcashExportFormats } from 'features/gcash/enums/gcash-export-formats.enum';

function* getStatistics(): Generator<
  CallEffect<AxiosResponse<GcashDocumentsStaticticsResponse>> | PutEffect,
  void,
  AxiosResponse<GcashDocumentsStaticticsResponse>
> {
  try {
    const response = yield call(getGcashStatistics);
    trackEvent(AmplitudeEvents.DASHBOARD_PAGE_VIEWED, {
      files_count: response.data.totalDocumentsCount,
      applications_count: response.data.totalClients,
      needs_review_count: response.data.documentsUnderReviewCount,
    });
    yield put(gcashActions.getGcashStatisticsSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(gcashActions.getGcashStatisticsFailure(getArrayApiErrors(error)));
  }
}
function* getLogs({
  payload,
}: PayloadAction<{ page: number; limit: number }>): Generator<
  CallEffect<AxiosResponse<PaginatedGcashActionLogsResponse>> | PutEffect,
  void,
  AxiosResponse<PaginatedGcashActionLogsResponse>
> {
  try {
    const response = yield call(getGcashLogs, payload);
    yield put(gcashActions.getGcashLogsSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(gcashActions.getGcashLogsFailure(getArrayApiErrors(error)));
  }
}

function* getApplications({
  payload,
}: PayloadAction<GetPaginatedGcashApplicationsParams>): Generator<
  CallEffect<AxiosResponse<GetPaginatedGcashApplicationsResponse>> | PutEffect,
  void,
  AxiosResponse<GetPaginatedGcashApplicationsResponse>
> {
  try {
    if (payload.search) {
      trackEvent(AmplitudeEvents.APPLICATION_SEARCH_TYPED, {
        query: payload.search,
      });
    }
    const response = yield call(getGcashApplications, payload);
    yield put(gcashActions.getGcashApplicationsSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      gcashActions.getGcashApplicationsFailure(getArrayApiErrors(error))
    );
  }
}

function* createApplication({
  payload,
}: PayloadAction<{ fullName: string }>): Generator<
  CallEffect<AxiosResponse<GcashApplicationResponse>> | PutEffect,
  void,
  AxiosResponse<GcashApplicationResponse>
> {
  try {
    const response = yield call(createGcashApplication, payload);
    yield put(gcashActions.createGcashApplicationSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      gcashActions.createGcashApplicationFailure(getArrayApiErrors(error))
    );
  }
}

function* getApplication({
  payload,
}: PayloadAction<string>): Generator<
  CallEffect<AxiosResponse<GcashApplicationResponse>> | PutEffect,
  void,
  AxiosResponse<GcashApplicationResponse>
> {
  try {
    const response = yield call(getGcashApplication, payload);
    yield put(gcashActions.getGcashApplicationSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      gcashActions.getGcashApplicationFailure(getArrayApiErrors(error))
    );
  }
}

function* getApplicationDocuments({
  payload,
}: PayloadAction<
  GetPaginatedGcashApplicationDocumentsParams & { applicationId: string }
>): Generator<
  | CallEffect<AxiosResponse<GetPaginatedGcashApplicationDocumentsResponse>>
  | PutEffect,
  void,
  AxiosResponse<GetPaginatedGcashApplicationDocumentsResponse>
> {
  try {
    const response = yield call(getGcashApplicationDocuments, payload);
    yield put(gcashActions.getGcashApplicationDocumentsSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      gcashActions.getGcashApplicationDocumentsFailure(getArrayApiErrors(error))
    );
  }
}

function* createApplicationDocument({
  payload,
}: PayloadAction<
  GcashCreateDocumentRequest & { applicationId: string }
>): Generator<
  CallEffect<AxiosResponse<GcashApplicationDocumentResponse>> | PutEffect,
  void,
  AxiosResponse<GcashApplicationDocumentResponse>
> {
  try {
    const file = payload.file as File;
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    trackEvent(AmplitudeEvents.DOCUMENT_UPLOAD_STARTED, {
      file_size: file.size,
      file_type: fileExtension,
    });
    const response = yield call(createGcashApplicationDocument, payload);
    trackEvent(AmplitudeEvents.DOCUMENT_UPLOAD_SUCCESS, {
      document_id: response.data.id,
    });
    yield put(
      gcashActions.createGcashApplicationDocumentSuccess(response.data)
    );
  } catch (error) {
    trackEvent(AmplitudeEvents.DOCUMENT_UPLOAD_FAILED, {
      error_message: extractErrorMessage(error),
    });
    toast.error(extractErrorMessage(error));
    yield put(
      gcashActions.createGcashApplicationDocumentFailure(
        getArrayApiErrors(error)
      )
    );
  }
}

function* createNewPage({
  payload,
}: PayloadAction<
  GcashCreatePageRequest & {
    applicationId: string;
    documentId: string;
  }
>): Generator<
  CallEffect<AxiosResponse<GcashDocumentPages>> | PutEffect,
  void,
  AxiosResponse<GcashDocumentPages>
> {
  try {
    const response = yield call(createGcashNewPage, payload);
    trackEvent(AmplitudeEvents.ADD_PAGE_UPLOAD_SUCCESS, {
      document_id: payload.documentId,
    });
    yield put(gcashActions.createGcashNewPageSuccess(response.data));
  } catch (error) {
    trackEvent(AmplitudeEvents.ADD_PAGE_UPLOAD_FAILED, {
      document_id: payload.documentId,
      error_message: extractErrorMessage(error),
    });
    toast.error(extractErrorMessage(error));
    yield put(gcashActions.createGcashNewPageFailure(getArrayApiErrors(error)));
  }
}

function* getDocumentInfo({
  payload,
}: PayloadAction<{
  applicationId: string;
  documentId: string;
}>): Generator<
  CallEffect<AxiosResponse<GcashApplicationDocumentResponse>> | PutEffect,
  void,
  AxiosResponse<GcashApplicationDocumentResponse>
> {
  try {
    const response = yield call(getGcashDocumentInfo, payload);
    yield put(gcashActions.getGcashDocumentInfoSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      gcashActions.getGcashDocumentInfoFailure(getArrayApiErrors(error))
    );
  }
}

function* changeDocumentStatus({
  payload,
}: PayloadAction<{
  status: GcashDocumentStatuses;
  reason: string;
  applicationId: string;
  documentId: string;
}>): Generator<
  CallEffect<AxiosResponse<GcashApplicationDocumentResponse>> | PutEffect,
  void,
  AxiosResponse<GcashApplicationDocumentResponse>
> {
  try {
    const response = yield call(changeGcashDocumentStatus, payload);
    if (payload.status === GcashDocumentStatuses.APPROVED) {
      trackEvent(AmplitudeEvents.APPROVE_DOCUMENT_SUCCESS, {
        reviewer_email: response.data.reviewedBy,
        reason: payload.reason,
      });
    } else if (payload.status === GcashDocumentStatuses.REJECTED) {
      trackEvent(AmplitudeEvents.REJECT_DOCUMENT_SUBMIT_SUCCESS, {
        reviewer_email: response.data.reviewedBy,
        reason: payload.reason,
      });
    }
    yield put(gcashActions.changeGcashDocumentStatusSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      gcashActions.changeGcashDocumentStatusFailure(getArrayApiErrors(error))
    );
  }
}

function* getDocumentPages({
  payload,
}: PayloadAction<{
  applicationId: string;
  documentId: string;
}>): Generator<
  CallEffect<AxiosResponse<GcashDocumentPages>> | PutEffect,
  void,
  AxiosResponse<GcashDocumentPages>
> {
  try {
    const response = yield call(getGcashDocumentPages, payload);
    yield put(gcashActions.getGcashDocumentPagesSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      gcashActions.getGcashDocumentPagesFailure(getArrayApiErrors(error))
    );
  }
}

function* getDocumentsByStatus({
  payload,
}: PayloadAction<PaginationGcashWithStatusRequest>): Generator<
  | CallEffect<AxiosResponse<PaginatedGcashDocumentsByStatusResponse>>
  | PutEffect,
  void,
  AxiosResponse<PaginatedGcashDocumentsByStatusResponse>
> {
  try {
    const response = yield call(getGcashDocumentsByStatus, payload);
    yield put(gcashActions.getDocumentsByStatusSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      gcashActions.getDocumentsByStatusFailure(getArrayApiErrors(error))
    );
  }
}

function* exportDocumentsByStatus({
  payload,
}: PayloadAction<GcashExportDocumentsRequest>): Generator<
  CallEffect<AxiosResponse<Blob>> | PutEffect,
  void,
  AxiosResponse<Blob>
> {
  try {
    const appliedFilters: string[] = [];
    if (payload.date?.startDate || payload.date?.endDate) {
      appliedFilters.push('date');
    }
    if (payload.filters?.type) {
      appliedFilters.push('type');
    }
    if (payload.format === GcashExportFormats.CSV) {
      trackEvent(AmplitudeEvents.REPORT_EXPORT_CSV_CLICKED, {
        filters: appliedFilters,
      });
    } else if (payload.format === GcashExportFormats.PDF) {
      trackEvent(AmplitudeEvents.REPORT_EXPORT_PDF_CLICKED, {
        filters: appliedFilters,
      });
    } else if (payload.format === GcashExportFormats.JSON) {
      trackEvent(AmplitudeEvents.REPORT_EXPORT_JSON_CLICKED, {
        filters: appliedFilters,
      });
    }
    const response = yield call(exportGcashDocumentByStatus, payload);

    const href = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    const fileName = `report-${new Date().toISOString()}.${payload.format}`;
    link.href = href;
    link.setAttribute('download', fileName);
    link.click();
    URL.revokeObjectURL(href);
    // The backend flags a capped export (row limit reached upstream) with
    // this header; without a notice the user assumes the file is complete.
    // CU-86bawra46.
    if (response.headers['x-result-truncated'] === 'true') {
      toast.warn(
        'This export hit the row limit and does not include all matching rows. Narrow the date range to export the rest.'
      );
    }
    yield put(gcashActions.exportDocumentsByStatusSuccess());
  } catch (error) {
    if (
      error.response?.data instanceof Blob &&
      error.response.data.type === 'application/json'
    ) {
      const text = yield error.response.data.text();
      const errorData = JSON.parse(text as unknown as string);
      toast.error(errorData.message || errorData.error || 'Export failed');
    } else {
      toast.error(extractErrorMessage(error));
    }
    yield put(
      gcashActions.exportDocumentsByStatusFailure(getArrayApiErrors(error))
    );
  }
}

function* exportSingleDocument({
  payload,
}: PayloadAction<GcashExportDocumentRequest>): Generator<
  CallEffect<AxiosResponse<Blob>> | PutEffect,
  void,
  AxiosResponse<Blob>
> {
  try {
    const response = yield call(exportGcashSingleDocument, payload);
    const href = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    const fileName = `document-data-${
      payload.documentId
    }-${new Date().toISOString()}.${payload.format}`;
    link.href = href;
    link.setAttribute('download', fileName);
    link.click();
    URL.revokeObjectURL(href);
    yield put(gcashActions.exportSingleDocumentSuccess());
  } catch (error) {
    if (
      error.response?.data instanceof Blob &&
      error.response.data.type === 'application/json'
    ) {
      const text = yield error.response.data.text();
      const errorData = JSON.parse(text as unknown as string);
      toast.error(errorData.message || errorData.error || 'Export failed');
    } else {
      toast.error(extractErrorMessage(error));
    }

    yield put(
      gcashActions.exportSingleDocumentFailure(getArrayApiErrors(error))
    );
  }
}

function* exportApplication({
  payload,
}: PayloadAction<string>): Generator<
  CallEffect<AxiosResponse<Blob>> | PutEffect,
  void,
  AxiosResponse<Blob>
> {
  try {
    const response = yield call(exportGcashApplication, payload);
    const href = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    const fileName = `application-${payload}-${new Date().toISOString()}.json`;
    link.href = href;
    link.setAttribute('download', fileName);
    link.click();
    URL.revokeObjectURL(href);
    yield put(gcashActions.exportApplicationSuccess());
  } catch (error) {
    if (
      error.response?.data instanceof Blob &&
      error.response.data.type === 'application/json'
    ) {
      const text = yield error.response.data.text();
      const errorData = JSON.parse(text as unknown as string);
      toast.error(errorData.message || errorData.error || 'Export failed');
    } else {
      toast.error(extractErrorMessage(error));
    }
    yield put(gcashActions.exportApplicationFailure(getArrayApiErrors(error)));
  }
}

function* checkDocumentsStatus({
  payload,
}: PayloadAction<string>): Generator<
  CallEffect<AxiosResponse<GcashDocumentsStatusResponse>> | PutEffect,
  void,
  AxiosResponse<GcashDocumentsStatusResponse>
> {
  try {
    const response = yield call(getGcashDocumentsStatus, payload);
    yield put(gcashActions.getGcashDocumentsStatusSuccess(response.data));
  } catch (error) {
    yield put(
      gcashActions.getGcashDocumentsStatusFailure(getArrayApiErrors(error))
    );
  }
}

function* getDocumentTypes(): Generator<
  CallEffect<AxiosResponse<GcashDocumentTypesResponse>> | PutEffect,
  void,
  AxiosResponse<GcashDocumentTypesResponse>
> {
  try {
    const response = yield call(getGcashDocumentTypes);
    yield put(gcashActions.getGcashDocumentTypesSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      gcashActions.getGcashDocumentTypesFailure(getArrayApiErrors(error))
    );
  }
}

function* gcashSaga() {
  yield takeLatest(gcashActions.getGcashStatisticsRequest, getStatistics);
  yield takeLatest(gcashActions.getGcashLogsRequest, getLogs);
  yield takeLatest(gcashActions.getGcashApplicationsRequest, getApplications);
  yield takeLatest(
    gcashActions.createGcashApplicationRequest,
    createApplication
  );
  yield takeLatest(gcashActions.getGcashApplicationRequest, getApplication);
  yield takeLatest(
    gcashActions.getGcashApplicationDocumentsRequest,
    getApplicationDocuments
  );
  yield takeLatest(
    gcashActions.createGcashApplicationDocumentRequest,
    createApplicationDocument
  );
  yield takeLatest(gcashActions.getGcashDocumentInfoRequest, getDocumentInfo);
  yield takeLatest(gcashActions.createGcashNewPageRequest, createNewPage);
  yield takeLatest(
    gcashActions.changeGcashDocumentStatusRequest,
    changeDocumentStatus
  );
  yield takeLatest(gcashActions.getGcashDocumentPagesRequest, getDocumentPages);
  yield takeLatest(
    gcashActions.getDocumentsByStatusRequest,
    getDocumentsByStatus
  );
  yield takeLatest(
    gcashActions.exportDocumentsByStatusRequest,
    exportDocumentsByStatus
  );
  yield takeLatest(
    gcashActions.exportSingleDocumentRequest,
    exportSingleDocument
  );
  yield takeLatest(gcashActions.exportApplicationRequest, exportApplication);
  yield takeLatest(
    gcashActions.getGcashDocumentsStatusRequest,
    checkDocumentsStatus
  );
  yield takeLatest(gcashActions.getGcashDocumentTypesRequest, getDocumentTypes);
}

export default gcashSaga;
